/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';

import { Car, CarDTO } from 'src/app/_model/car';
import { CarsDaoService } from '../../dao/cars-dao.service';
import { CarsService } from '../cars.service';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads Cars
 */
@Injectable()
export class CarsLogicService implements CarsService {
  public cars$ = new BehaviorSubject<Car[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private carsDaoService: CarsDaoService,
    private authenticationService: AuthenticationService,
    private appParameter: AppParameterService
  ) {
    this.initialize();
  }

  public getCarById(id: IMSId): Car {
    if (id === undefined) {
      return undefined
    }
    
    return this.cars$.getValue().find((car) => {
      if (car.id.imsId === id.imsId && car.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset() {
    this.carsDaoService.reset();
  }

  public async clear() {
    await this.carsDaoService.clear();
    this.cars$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteCars(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}


  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalCars();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalCars() {
    try {
      this.logger.log(this.constructor.name, '- Load local Cars');
      let cars: Car[] = await this.carsDaoService.getCars();

      this.cars$.next(cars);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Cars: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteCars(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load new Cars from IMS');

      this.logger.log(
        this.constructor.name,
        '- Load remote Cars from IMS'
      );

      try {
        let cars: Car[] = [];
        let carsData: CarDTO[] = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'synccars',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );
        
        this.logger.log(
          this.constructor.name,
          'Downloaded Cars: ',
          carsData.length,
          'Total'
        );

        for (const carDto of carsData) {
          cars.push(Car.fromDto(carDto, undefined, 
          this.appParameter.getFormatLabelForObject('ims/Car')));
        }

        await this.carsDaoService.saveCars(cars);
        this.cars$.next(cars);

      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Cars: ',
          error?.message
        );
        throw error;
      }
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
